<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useNotifications, isPermissionError} from '@/utils/toast.js';
import { useDirection } from '@/composables/useDirection';
import { useLanguageStore } from '@/stores/language';
import defaultAvatar from '@/assets/images/favicon/favicon-green-200.png';

const props = defineProps({
    class: {
        type: String,
        default: ''
    }
});

const router = useRouter();
const authStore = useAuthStore();
const toast = useNotifications();
const { isRtl } = useDirection();
const { t } = useLanguageStore();
const isLoggingOut = ref(false);
const emailCopied = ref(false);

const copyEmail = async () => {
    const email = authStore.user?.email;
    if (!email) return;
    try {
        await navigator.clipboard.writeText(email);
        emailCopied.value = true;
        setTimeout(() => { emailCopied.value = false; }, 2000);
    } catch {}
};

const userRoleDisplay = computed(() => {
    // Try roles array first
    const roles = authStore.userRoles;
    if (roles && roles.length > 0) {
        return roles.map(r => r.name || r).join(', ');
    }
    // Fallback: direct field on user object
    const user = authStore.user;
    if (user?.role) return user.role;
    if (user?.role_name) return user.role_name;
    return '';
});

// Always refresh user from server when dropdown opens to ensure roles are up-to-date
onMounted(async () => {
    try {
        await authStore.getCurrentUser();
    } catch {}
});

const buttonClass = 'w-full px-3 py-2 rounded-lg flex items-center gap-2 font-medium text-surface-500 hover:text-surface-950 dark:hover:text-surface-0 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors duration-150';

const handleLogout = async () => {
    if (isLoggingOut.value) return;
    
    isLoggingOut.value = true;
    try {
        const response = await authStore.logout();
        toast.success(t('You have been logged out successfully'), t('Logout Successful'));
        const ssoLogoutUrl = response?.data?.logout_url;
        if (ssoLogoutUrl) {
            window.location.assign(ssoLogoutUrl);
            return;
        }
        router.push('/auth/login');
    } catch (error) {
        console.error('Logout error:', error);
        if (!isPermissionError(error)) {
          toast.error(t('Failed to logout. Please try again.'), t('Logout Error'));
        }
    } finally {
        isLoggingOut.value = false;
    }
};

const handleSettings = () => {
    router.push('/profile/settings');
};

</script>

<template>
    <div
        :class="[props.class, isRtl ? 'rtl-dropdown' : '']"
        class="p-2 rounded-2xl bg-surface-0 dark:bg-surface-950 shadow-[0px_129.205px_36.493px_0px_rgba(44,54,87,0.00),_0px_82.849px_33.534px_0px_rgba(44,54,87,0.01),_0px_46.356px_27.616px_0px_rgba(44,54,87,0.03),_0px_20.712px_20.712px_0px_rgba(44,54,87,0.05),_0px_4.932px_11.836px_0px_rgba(44,54,87,0.06)] dark:shadow-stroke"
        :dir="isRtl ? 'rtl' : 'ltr'"
    >
        <!-- User Info Header -->
        <div class="px-3 py-3 border-b border-surface-200 dark:border-surface-700 mb-1">
            <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                    <img :src="authStore.user?.avatar_url || defaultAvatar" alt="Profile" width="40" height="40" class="w-full h-full object-cover" />
                </div>
                <div class="min-w-0 flex-1">
                    <!-- Role badge above name -->
                    <span
                        v-if="userRoleDisplay"
                        class="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full mb-1 bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 truncate max-w-full"
                    >{{ userRoleDisplay }}</span>
                    <p class="text-sm font-semibold text-surface-900 dark:text-surface-0 truncate leading-tight">{{ authStore.userFullName || t('User') }}</p>
                    <!-- Email with copy on hover -->
                    <div
                        class="group flex items-center gap-1 cursor-pointer mt-0.5"
                        @click="copyEmail"
                        :title="emailCopied ? t('Copied!') : t('Copy email')"
                    >
                        <p class="text-xs text-surface-400 dark:text-surface-500 truncate group-hover:text-primary-500 transition-colors">{{ authStore.user?.email }}</p>
                        <i
                            :class="emailCopied ? 'pi pi-check text-green-500' : 'pi pi-copy text-surface-400 group-hover:text-primary-500'"
                            class="text-[10px] shrink-0 opacity-0 group-hover:opacity-100 transition-all"
                        /></div>
                </div>
            </div>
        </div>

        <!-- Menu Items -->
        <div class="space-y-1">
            <button :class="buttonClass" @click="handleSettings">
                <i class="pi pi-cog" />
                <span>{{ t('Settings') }}</span>
            </button>
            <button :class="buttonClass" @click="handleLogout" :disabled="isLoggingOut">
                <i :class="isLoggingOut ? 'pi pi-spin pi-spinner' : 'pi pi-sign-out'" />
                <span>{{ isLoggingOut ? t('Logging out...') : t('Log Out') }}</span>
            </button>
        </div>
    </div>
</template>

<style scoped>
.rtl-dropdown button {
    flex-direction: row-reverse;
}
</style>
